import { map, catchError, switchMap, filter, mergeMap } from "rxjs/operators";
import { of, forkJoin } from "rxjs";
import type { RootEpic } from "@/store/types";
import {
  getOrganizationUsersRequest,
  getOrganizationUsersSuccess,
  getOrganizationUsersFailure,
} from "./organizationUsersSlice";
import { UploadFileService } from "@/features/upload/services/UploadFileService";

export const getOrganizationUsersEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter((action) => action.type === getOrganizationUsersRequest.type),
    switchMap((action: any) => {
      const { orgId, page = 1, pageSize = 100 } = action.payload;
      //console.log("defaultOrganization", action.payload);
      
      // First, fetch the first page to get pageCount
      return UploadFileService.Get.getOrganizationUsers({
        orgId,
        page,
        pageSize,
      }).pipe(
        mergeMap((firstPageRes: any) => {
          const { pageCount, results } = firstPageRes;
          
          // If only one page, return immediately
          if (!pageCount || pageCount <= 1) {
            return of(getOrganizationUsersSuccess(firstPageRes));
          }
          
          // If multiple pages, fetch all remaining pages
          const remainingPages = Array.from(
            { length: pageCount - 1 },
            (_, i) => i + 2
          ); // pages 2, 3, 4, etc.
          
          const additionalRequests = remainingPages.map((pageNum) =>
            UploadFileService.Get.getOrganizationUsers({
              orgId,
              page: pageNum,
              pageSize,
            })
          );
          
          // Fetch all pages in parallel
          return forkJoin(additionalRequests).pipe(
            map((additionalPages: any[]) => {
              // Combine all results
              const allResults = [
                ...results,
                ...additionalPages.flatMap((page) => page.results || []),
              ];
              
              // Return combined data with updated pagination info
              return getOrganizationUsersSuccess({
                ...firstPageRes,
                results: allResults,
                queryCount: allResults.length,
                lastRowIndex: allResults.length,
              });
            }),
            catchError((err) =>
              of(
                getOrganizationUsersFailure(
                  err?.message || "Error fetching additional pages"
                )
              )
            )
          );
        }),
        catchError((err) =>
          of(
            getOrganizationUsersFailure(
              err?.message || "Error fetching organization users"
            )
          )
        )
      );
    })
  );

export const organizationUsersEpics = [getOrganizationUsersEpic$];
